import { Card, CardContent } from "@bowfast/ui/components/card";
import { cn } from "@bowfast/ui/lib/utils";
import { SailboatIcon } from "lucide-react";
import { gradientForId } from "../../lib/gradients";
import type { VesselListMember } from "../../pages/vessels/vessels.types";
import { activeExpeditionName } from "../../pages/vessels/vessels.utils";

interface VesselsAtSeaSpotlightProps {
  readonly vessels: VesselListMember[];
}

export function VesselsAtSeaSpotlight({ vessels }: Readonly<VesselsAtSeaSpotlightProps>) {
  const atSea = vessels.filter((v) => activeExpeditionName(v) !== undefined);

  if (atSea.length === 0) return null;

  return (
    <Card className="relative overflow-hidden border-0 bg-abyss-900 text-white">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 80% 40%, rgba(4,124,199,0.2) 0%, transparent 50%), radial-gradient(ellipse at 20% 90%, rgba(20,184,166,0.1) 0%, transparent 40%)",
        }}
        aria-hidden
      />
      <CardContent className="relative z-10 flex flex-wrap items-center gap-5 py-4">
        <div className="flex -space-x-2">
          {atSea.map((v) => (
            <div
              key={v.id}
              className={cn(
                "flex size-9 items-center justify-center rounded-full border-2 border-abyss-900 bg-linear-to-br",
                gradientForId(v.id),
              )}
            >
              <SailboatIcon className="size-4 text-white/80" />
            </div>
          ))}
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-shallow-400 opacity-60" />
              <span className="relative inline-flex size-2 rounded-full bg-shallow-400" />
            </span>
            <p className="text-sm font-semibold">{atSea.length} vessels at sea</p>
          </div>
          <p className="mt-0.5 text-xs text-white/50">
            {atSea.map((v) => `${v.name} — ${activeExpeditionName(v)}`).join(" · ")}
          </p>
        </div>

        <div className="flex items-center gap-4 text-xs">
          <div className="text-center">
            <p className="font-heading text-lg font-bold tabular-nums">{atSea.length}</p>
            <p className="text-white/40">vessels at sea</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
